import React, { useState, useEffect, useCallback, useRef } from "react";
import { callCentreAPI, formatError } from "../utils/api";
import { useAuth } from "../contexts/AuthContext";
import {
  Phone, Star, CheckCircle, AlertTriangle, RefreshCw, History,
  Search, ChevronRight, X, BarChart3, Loader, Sparkles, ChevronDown,
  FileText, Flag, TrendingUp, Printer
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";

const FEEDBACK_CATS = ["Food Quality","Quantity","Delivery Time","Vessel Condition","Staff Behaviour","No Issues"];
const SEVERITIES = ["Low","Medium","High"];
const SEV_COLORS = { Low:"bg-gray-100 text-gray-600", Medium:"bg-orange-100 text-orange-700", High:"bg-red-100 text-red-700" };
const CAT_COLORS = {
  "Food Quality":"bg-red-50 text-red-700", "Quantity":"bg-orange-50 text-orange-700",
  "Delivery Time":"bg-amber-50 text-amber-700", "Vessel Condition":"bg-blue-50 text-blue-700",
  "Staff Behaviour":"bg-purple-50 text-purple-700", "No Issues":"bg-green-50 text-green-700",
};

function StarRating({ value, onChange }) {
  return (
    <div className="flex gap-1">
      {[1,2,3,4,5].map(n => (
        <button key={n} onClick={() => onChange?.(n)}
          className={`text-2xl min-w-[44px] min-h-[44px] flex items-center justify-center ${
            value>=n ? "text-amber-400" : "text-[#E5E1DB]"
          } ${onChange ? "" : "cursor-default"}`}
          data-testid={`star-${n}`}>★</button>
      ))}
    </div>
  );
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", {day:"2-digit",month:"short",hour:"2-digit",minute:"2-digit",hour12:true,timeZone:"Asia/Kolkata"});
  } catch { return iso; }
}

// ─── SCHOOL HISTORY PANEL ─────────────────────────────────────────────────────
function SchoolHistoryPanel({ school, onLogAnother, onBack }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    callCentreAPI.getSchoolHistory(school.school_id)
      .then(r => setHistory(r.data||[]))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [school.school_id]);

  return (
    <div className="space-y-4 max-w-2xl">
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="text-sm font-bold text-[#E8956D]">← Back</button>
        <div className="flex-1">
          <p className="font-bold text-[#3A322C] text-lg">{school.school_name}</p>
          <p className="text-xs text-[#8A7E73]">Route {school.route_number} · {school.mandal}</p>
        </div>
        <button onClick={onLogAnother}
          className="flex items-center gap-2 bg-[#E8956D] text-white rounded-xl px-4 py-2.5 text-sm font-bold"
          data-testid="log-another-btn">
          <Phone size={14}/> Log Another Call
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-8"><Loader size={20} className="animate-spin text-[#E8956D]"/></div>
      ) : history.length === 0 ? (
        <div className="bg-white border border-[#E5E1DB] rounded-2xl p-6 text-center text-[#8A7E73]">
          No call history for this school yet.
        </div>
      ) : (
        <div className="space-y-3">
          {history.map((h,i) => (
            <div key={i} className="bg-white border border-[#E5E1DB] rounded-2xl p-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs text-[#8A7E73]">{fmtDateTime(h.created_at)}</p>
                  <p className="text-sm font-bold text-[#3A322C] mt-0.5">{h.called_by}</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap justify-end">
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${h.status==="Answered"?"bg-green-100 text-green-700":"bg-gray-100 text-gray-600"}`}>{h.status}</span>
                  {h.feedback_category && <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${CAT_COLORS[h.feedback_category]||"bg-[#F4F3F0] text-[#5C5147]"}`}>{h.feedback_category}</span>}
                  {h.is_complaint && <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${SEV_COLORS[h.complaint_severity]||SEV_COLORS.Low}`}>{h.complaint_severity}</span>}
                </div>
              </div>
              {h.rating && <div className="mt-2"><StarRating value={h.rating}/></div>}
              {h.notes && <p className="text-sm text-[#5C5147] mt-2 bg-[#FAFAF8] rounded-lg px-3 py-2">{h.notes}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── LOG CALL TAB ─────────────────────────────────────────────────────────────
function LogCallTab({ onLogged }) {
  const [dailyList, setDailyList] = useState({ schools:[], called:0, uncalled:0, total:0 });
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [viewHistory, setViewHistory] = useState(false);
  const [form, setForm] = useState({ status:"Answered", spoke_with:"", feedback_category:"", rating:null, notes:"", is_complaint:false, complaint_severity:"Low" });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(true);
  const [calledExpanded, setCalledExpanded] = useState(false);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      // FIX 3/8: fetch ALL schools (called + uncalled) so calledFiltered is never empty
      const r = await callCentreAPI.getDailyList({ limit: 2000 });
      setDailyList(r.data || {});
    }
    catch(e) {}
    setLoading(false);
  }, []);

  useEffect(() => { fetchList(); }, [fetchList]);

  const allSchools = dailyList.schools || [];
  const uncalledFiltered = allSchools.filter(s =>
    !s.is_called && (!search || s.school_name.toLowerCase().includes(search.toLowerCase()) || String(s.route_number).includes(search))
  );
  const calledFiltered = allSchools.filter(s =>
    s.is_called && (!search || s.school_name.toLowerCase().includes(search.toLowerCase()) || String(s.route_number).includes(search))
  );

  const handleSave = async () => {
    if (!selected) return;
    setSaving(true); setMsg("");
    try {
      // FIX 2: explicitly include feedback_category
      const payload = {
        school_id: selected.school_id,
        school_name: selected.school_name,
        route_number: selected.route_number,
        status: form.status,
        spoke_with: form.spoke_with,
        feedback_category: form.feedback_category,   // explicit — NOT spread
        rating: form.rating,
        notes: form.notes,
        is_complaint: form.is_complaint,
        complaint_severity: form.complaint_severity,
      };
      const res = await callCentreAPI.logCall(payload);
      const taskCreated = res.data?.task_created;
      setMsg(taskCreated ? "Call logged + Task created for RC!" : "Call logged!");
      onLogged?.();
      setSelected(null); setViewHistory(false);
      setForm({ status:"Answered", spoke_with:"", feedback_category:"", rating:null, notes:"", is_complaint:false, complaint_severity:"Low" });
      await fetchList();
    } catch(e) { setMsg("Error: "+formatError(e)); }
    setSaving(false);
  };

  // FIX 8: arrow click on already-called school → history panel
  const handleRowClick = (s) => {
    setSelected(s);
    setViewHistory(s.is_called);
    setForm({ status:"Answered", spoke_with:"", feedback_category:"", rating:null, notes:"", is_complaint:false, complaint_severity:"Low" });
    setMsg("");
  };

  // School history panel
  if (selected && viewHistory) return (
    <SchoolHistoryPanel
      school={selected}
      onLogAnother={() => setViewHistory(false)}
      onBack={() => { setSelected(null); setViewHistory(false); }}
    />
  );

  // Call form
  if (selected && !viewHistory) return (
    <div className="space-y-4 max-w-2xl">
      <button onClick={() => { setSelected(null); setViewHistory(false); }} className="flex items-center gap-2 text-sm font-bold text-[#E8956D]">← Back</button>
      <div className="bg-white border border-[#E5E1DB] rounded-2xl p-5 space-y-5">
        <div>
          <p className="font-bold text-[#3A322C] text-xl">{selected.school_name}</p>
          <p className="text-sm text-[#8A7E73]">Route {selected.route_number} · {selected.mandal}</p>
        </div>

        <div>
          <label className="text-xs font-bold uppercase text-[#8A7E73] mb-2 block">Call Status *</label>
          <div className="flex gap-2">
            {["Answered","No Answer","Called Back Later"].map(s => (
              <button key={s} onClick={() => setForm(f=>({...f,status:s}))}
                className={`flex-1 py-2.5 rounded-xl text-sm font-bold border-2 ${form.status===s?"border-[#E8956D] bg-[#E8956D]/10 text-[#E8956D]":"border-[#E5E1DB] text-[#5C5147]"}`}>{s}</button>
            ))}
          </div>
        </div>

        {form.status==="Answered" && (
          <>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-bold uppercase text-[#8A7E73] mb-2 block">Spoke With</label>
                <input className="w-full border-2 border-[#E5E1DB] rounded-xl px-4 py-3 outline-none focus:border-[#E8956D]"
                  placeholder="Name + Designation" value={form.spoke_with} onChange={e=>setForm(f=>({...f,spoke_with:e.target.value}))}/>
              </div>
              <div>
                <label className="text-xs font-bold uppercase text-[#8A7E73] mb-2 block">Feedback Category</label>
                <select className="w-full border-2 border-[#E5E1DB] rounded-xl px-4 py-3 outline-none focus:border-[#E8956D]"
                  value={form.feedback_category}
                  onChange={e=>setForm(f=>({...f,feedback_category:e.target.value}))}
                  data-testid="feedback-category-select">
                  <option value="">Select…</option>
                  {FEEDBACK_CATS.map(c=><option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>

            <div>
              <label className="text-xs font-bold uppercase text-[#8A7E73] mb-2 block">Rating</label>
              <StarRating value={form.rating||0} onChange={v=>setForm(f=>({...f,rating:v}))}/>
            </div>

            <div>
              <label className="text-xs font-bold uppercase text-[#8A7E73] mb-2 block">Notes</label>
              <textarea rows={3} className="w-full border-2 border-[#E5E1DB] rounded-xl px-4 py-3 outline-none focus:border-[#E8956D] resize-none"
                placeholder="Detailed observations…" value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))}/>
            </div>

            <div>
              <div className="flex items-center gap-4">
                <span className="text-sm font-bold text-[#3A322C]">Complaint?</span>
                <button onClick={() => setForm(f=>({...f,is_complaint:!f.is_complaint}))}
                  className={`relative w-14 h-7 rounded-full transition-colors ${form.is_complaint?"bg-[#C2624E]":"bg-[#E5E1DB]"}`}
                  data-testid="complaint-toggle">
                  <span className={`absolute top-0.5 w-6 h-6 rounded-full bg-white shadow transition-transform ${form.is_complaint?"translate-x-7":"translate-x-0.5"}`}/>
                </button>
              </div>
              {form.is_complaint && (
                <div className="mt-3">
                  <label className="text-xs font-bold uppercase text-[#8A7E73] mb-2 block">Severity</label>
                  <div className="flex gap-3">
                    {SEVERITIES.map(s=>(
                      <button key={s} onClick={()=>setForm(f=>({...f,complaint_severity:s}))}
                        className={`flex-1 py-2.5 rounded-xl text-sm font-bold border-2 ${form.complaint_severity===s?(s==="High"?"border-red-500 bg-red-50 text-red-700":s==="Medium"?"border-amber-400 bg-amber-50 text-amber-700":"border-green-400 bg-green-50 text-green-700"):"border-[#E5E1DB] text-[#5C5147]"}`}>{s}</button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {msg && <div className={`rounded-xl px-4 py-3 text-sm font-bold ${msg.startsWith("Error")?"bg-[#FBEAE7] text-[#C2624E]":"bg-[#EDF2EE] text-[#637C64]"}`}>{msg}</div>}

        <button onClick={handleSave} disabled={saving}
          className="w-full bg-[#E8956D] text-white rounded-xl py-4 font-bold text-base flex items-center justify-center gap-2 disabled:opacity-60"
          data-testid="save-call-btn">
          {saving?<Loader size={18} className="animate-spin"/>:<Phone size={18}/>}
          {saving?"Saving…":"Save & Next"}
        </button>
      </div>
    </div>
  );

  // School list
  return (
    <div className="space-y-4">
      {/* Progress bar — always visible */}
      {dailyList.total > 0 && (
        <div className="bg-white border border-[#E5E1DB] rounded-2xl p-5" data-testid="progress-bar">
          <div className="flex items-center justify-between mb-2">
            <p className="font-bold text-[#3A322C]">{dailyList.called} of {dailyList.total} schools called today</p>
            <span className="text-sm text-[#8A7E73]">{Math.round((dailyList.called/dailyList.total)*100)}%</span>
          </div>
          <div className="h-3 bg-[#F4F3F0] rounded-full overflow-hidden">
            <div className="h-full bg-[#E8956D] rounded-full transition-all" style={{width:`${(dailyList.called/dailyList.total)*100}%`}}/>
          </div>
          <p className="text-xs text-[#8A7E73] mt-1">{dailyList.uncalled} remaining</p>
        </div>
      )}

      <div className="relative">
        <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8A7E73]"/>
        <input className="w-full border border-[#E5E1DB] rounded-xl pl-10 pr-4 py-3 outline-none focus:border-[#E8956D]"
          placeholder="Search school or route…" value={search} onChange={e=>setSearch(e.target.value)}/>
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><Loader size={24} className="animate-spin text-[#E8956D]"/></div>
      ) : (
        <div className="space-y-3">
          {/* Uncalled schools at top */}
          <div className="bg-white border border-[#E5E1DB] rounded-2xl overflow-hidden">
            <div className="px-4 py-3 bg-[#FAFAF8] border-b border-[#F4F3F0]">
              <p className="text-sm font-bold text-[#3A322C]">{uncalledFiltered.length} uncalled</p>
            </div>
            {uncalledFiltered.length === 0 ? (
              <div className="p-6 text-center text-[#8A7E73] text-sm">All schools called today!</div>
            ) : (
              <div className="divide-y divide-[#F4F3F0] max-h-[400px] overflow-y-auto">
                {uncalledFiltered.map(s => (
                  <button key={s.school_id} onClick={() => handleRowClick(s)}
                    className="w-full flex items-center px-5 py-4 hover:bg-[#FAFAF8] text-left"
                    data-testid={`school-call-${s.school_id}`}>
                    <div className="w-10 h-10 rounded-xl bg-[#E8956D]/10 text-[#E8956D] flex items-center justify-center text-xs font-bold mr-4 flex-shrink-0">
                      {s.route_number}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-[#3A322C] text-sm truncate">{s.school_name}</p>
                      <p className="text-xs text-[#8A7E73]">{s.mandal}</p>
                    </div>
                    <ChevronRight size={16} className="text-[#C4BAAF]"/>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* FIX 3: Called Today — collapsible section at bottom */}
          {calledFiltered.length > 0 && (
            <div className="bg-white border border-[#E5E1DB] rounded-2xl overflow-hidden">
              <button
                className="w-full flex items-center justify-between px-4 py-3 bg-[#EDF2EE] text-left"
                onClick={() => setCalledExpanded(!calledExpanded)}
                data-testid="called-today-toggle">
                <p className="text-sm font-bold text-[#637C64]">Called Today ({calledFiltered.length})</p>
                <ChevronDown size={16} className={`text-[#637C64] transition-transform ${calledExpanded?"rotate-180":""}`}/>
              </button>
              {calledExpanded && (
                <div className="divide-y divide-[#F4F3F0] max-h-[300px] overflow-y-auto">
                  {calledFiltered.map(s => (
                    <button key={s.school_id} onClick={() => handleRowClick(s)}
                      className="w-full flex items-center px-5 py-4 hover:bg-[#FAFAF8] text-left"
                      data-testid={`school-called-${s.school_id}`}>
                      <div className="w-10 h-10 rounded-xl bg-[#637C64]/10 text-[#637C64] flex items-center justify-center text-xs font-bold mr-4 flex-shrink-0">
                        {s.route_number}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-[#3A322C] text-sm truncate">{s.school_name}</p>
                        <p className="text-xs text-[#8A7E73]">{s.mandal}</p>
                      </div>
                      {/* FIX 3: Green "Called ✓" badge */}
                      <span className="flex items-center gap-1 text-xs font-bold text-[#637C64] bg-[#EDF2EE] px-2 py-1 rounded-full" data-testid={`called-badge-${s.school_id}`}>
                        <CheckCircle size={12}/>Called ✓
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── DASHBOARD TAB ─────────────────────────────────────────────────────────────
function DashboardTab({ refresh }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uncalled, setUncalled] = useState(null);
  const [routeFilter, setRouteFilter] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [schoolHistory, setSchoolHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [allSchools, setAllSchools] = useState([]);

  useEffect(() => {
    callCentreAPI.getDashboard().then(r => setData(r.data)).catch(()=>{}).finally(() => setLoading(false));
    callCentreAPI.getUncalled({}).then(r => { setUncalled(r.data); setAllSchools(r.data?.uncalled||[]); }).catch(()=>{});
  }, [refresh]);

  const handleRouteFilter = async (rn) => {
    setRouteFilter(rn);
    try {
      const r = await callCentreAPI.getUncalled(rn ? {route_number: rn} : {});
      setUncalled(r.data);
    } catch(e) {}
  };

  // Section C: quick school search
  useEffect(() => {
    if (!searchQuery.trim()) { setSearchResults([]); return; }
    const q = searchQuery.toLowerCase();
    const r = callCentreAPI.getDailyList({limit:20}).then ? null : null;
    // Search from the uncalled list and also search by name
    const results = (allSchools).filter(s =>
      s.school_name.toLowerCase().includes(q)
    ).slice(0,10);
    setSearchResults(results);
  }, [searchQuery, allSchools]);

  const loadSchoolHistory = async (s) => {
    setSelectedSchool(s); setHistoryLoading(true);
    try {
      const r = await callCentreAPI.getSchoolHistory(s.school_id);
      setSchoolHistory(r.data||[]);
    } catch(e) { setSchoolHistory([]); }
    setHistoryLoading(false);
  };

  if (loading) return <div className="flex justify-center py-16"><Loader size={24} className="animate-spin text-[#E8956D]"/></div>;
  if (!data) return null;

  return (
    <div className="space-y-5">
      {/* Section C: Quick School Search — at top */}
      <div className="bg-white border border-[#E5E1DB] rounded-2xl p-5 space-y-3">
        <p className="font-bold text-[#3A322C] text-sm uppercase tracking-wide">Quick School Search</p>
        <div className="relative">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8A7E73]"/>
          <input className="w-full border border-[#E5E1DB] rounded-xl pl-10 pr-4 py-3 outline-none focus:border-[#E8956D]"
            placeholder="Search school by name…" value={searchQuery} onChange={e=>{setSearchQuery(e.target.value); setSelectedSchool(null);}}
            data-testid="school-search"/>
        </div>
        {searchResults.length > 0 && !selectedSchool && (
          <div className="border border-[#E5E1DB] rounded-xl overflow-hidden">
            {searchResults.map(s => (
              <button key={s.school_id} onClick={() => loadSchoolHistory(s)}
                className="w-full flex items-center px-4 py-3 hover:bg-[#FAFAF8] text-left border-b border-[#F4F3F0] last:border-0">
                <span className="text-sm font-medium text-[#3A322C] flex-1">{s.school_name}</span>
                <span className="text-xs text-[#8A7E73]">Route {s.route_number}</span>
              </button>
            ))}
          </div>
        )}
        {selectedSchool && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="font-bold text-[#3A322C]">{selectedSchool.school_name}</p>
              <button onClick={() => { setSelectedSchool(null); setSearchQuery(""); }} className="text-xs text-[#8A7E73] hover:text-[#C2624E]">×</button>
            </div>
            {historyLoading ? <div className="flex justify-center py-4"><Loader size={18} className="animate-spin text-[#E8956D]"/></div>
            : schoolHistory.length === 0 ? <p className="text-sm text-[#8A7E73]">No call history for this school.</p>
            : schoolHistory.map((h,i) => (
              <div key={i} className="bg-[#FAFAF8] border border-[#E5E1DB] rounded-xl p-4">
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div>
                    <p className="text-xs text-[#8A7E73]">{fmtDateTime(h.created_at)}</p>
                    <p className="text-sm font-bold text-[#3A322C]">{h.called_by}</p>
                  </div>
                  <div className="flex gap-1 flex-wrap">
                    {h.feedback_category && <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${CAT_COLORS[h.feedback_category]||"bg-[#F4F3F0] text-[#5C5147]"}`}>{h.feedback_category}</span>}
                    {h.is_complaint && <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${SEV_COLORS[h.complaint_severity]||SEV_COLORS.Low}`}>{h.complaint_severity}</span>}
                  </div>
                </div>
                {h.rating && <div className="mt-1"><StarRating value={h.rating}/></div>}
                {h.notes && <p className="text-xs text-[#5C5147] mt-1">{h.notes}</p>}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          {label:"Called Today", v:data.called_today, color:"text-[#E8956D]"},
          {label:"Remaining",    v:data.uncalled_today, color:"text-[#5C5147]"},
          {label:"Complaints",   v:data.complaints_today, color:"text-[#C2624E]"},
          {label:"Avg Rating",   v:data.avg_rating_today ? `${data.avg_rating_today}★` : "—", color:"text-amber-500"},
        ].map(s => (
          <div key={s.label} className="bg-white border border-[#E5E1DB] rounded-2xl p-4 text-center">
            <p className={`text-3xl font-extrabold ${s.color}`}>{s.v}</p>
            <p className="text-xs text-[#8A7E73] mt-1 font-medium">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Section A: Recent Complaints */}
      <div className="bg-white border border-[#E5E1DB] rounded-2xl overflow-hidden">
        <div className="px-5 py-3 border-b border-[#F4F3F0]">
          <p className="font-bold text-[#3A322C]">Recent Complaints (last 24 hours)</p>
        </div>
        {!data.recent_complaints?.length ? (
          <div className="p-6 text-center text-[#8A7E73] text-sm" data-testid="no-complaints-msg">No complaints logged today.</div>
        ) : (
          <div className="divide-y divide-[#F4F3F0]">
            {data.recent_complaints.map(c => (
              <div key={c.id} className="px-5 py-4" data-testid={`complaint-row-${c.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-[#3A322C] text-sm">{c.school_name}</p>
                    <p className="text-xs text-[#8A7E73]">Route {c.route_number} · {c.coordinator_name || "—"}</p>
                  </div>
                  <div className="flex gap-1 flex-wrap">
                    {c.feedback_category && <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${CAT_COLORS[c.feedback_category]||"bg-[#F4F3F0] text-[#5C5147]"}`}>{c.feedback_category}</span>}
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${SEV_COLORS[c.complaint_severity]||SEV_COLORS.Low}`}>{c.complaint_severity}</span>
                  </div>
                </div>
                {c.notes && <p className="text-xs text-[#8A7E73] mt-1 line-clamp-1">{c.notes}</p>}
                <p className="text-[10px] text-[#C4BAAF] mt-1">{fmtDateTime(c.created_at)}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Section B: Schools Not Yet Called */}
      <div className="bg-white border border-[#E5E1DB] rounded-2xl overflow-hidden">
        <div className="px-5 py-3 border-b border-[#F4F3F0] flex items-center justify-between gap-3 flex-wrap">
          <p className="font-bold text-[#3A322C]">{uncalled?.total_uncalled||0} schools remaining today</p>
          <select className="border border-[#E5E1DB] rounded-lg px-3 py-1.5 text-sm outline-none focus:border-[#E8956D]"
            value={routeFilter} onChange={e => handleRouteFilter(e.target.value)}
            data-testid="route-filter">
            <option value="">All Routes</option>
            {(uncalled?.route_numbers||[]).slice(0,30).map(rn => <option key={rn} value={rn}>Route {rn}</option>)}
          </select>
        </div>
        {!uncalled?.uncalled?.length ? (
          <div className="p-6 text-center text-[#8A7E73] text-sm">All schools called today!</div>
        ) : (
          <div className="divide-y divide-[#F4F3F0] max-h-[280px] overflow-y-auto">
            {(uncalled.uncalled||[]).slice(0,20).map(s => (
              <div key={s.school_id} className="flex items-center px-5 py-3" data-testid={`uncalled-row-${s.school_id}`}>
                <div className="w-9 h-9 rounded-lg bg-[#E8956D]/10 text-[#E8956D] flex items-center justify-center text-xs font-bold mr-3 flex-shrink-0">{s.route_number}</div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#3A322C] truncate">{s.school_name}</p>
                  <p className="text-xs text-[#8A7E73]">{s.mandal}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── AI ANALYSIS TAB ──────────────────────────────────────────────────────────
// FIX 7: Color-coded cards + trend charts + PDF export
function AnalysisTab() {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const printRef = useRef(null);

  const run = async () => {
    setLoading(true); setError("");
    try { const r = await callCentreAPI.runAnalysis(); setResult(r.data); }
    catch(e) { setError(formatError(e)); }
    setLoading(false);
  };

  const handlePrint = () => {
    const content = printRef.current;
    if (!content) return;
    const win = window.open("", "_blank");
    win.document.write(`
      <html><head><title>Call Centre Analysis Report</title>
      <style>
        body { font-family: sans-serif; padding: 24px; color: #333; }
        h1 { font-size: 20px; margin-bottom: 16px; }
        .card { border: 2px solid #ccc; border-radius: 8px; padding: 16px; margin-bottom: 16px; }
        .yellow { border-color: #f59e0b; background: #fffbeb; }
        .red { border-color: #ef4444; background: #fef2f2; }
        .flag { display: inline-block; font-size: 11px; font-weight: bold; padding: 2px 8px; border-radius: 12px; margin: 2px; }
        .green-banner { background:#dcfce7; color:#166534; padding:16px; border-radius:8px; text-align:center; font-weight:bold; }
        @media print { button { display:none; } }
      </style></head><body>
      ${content.innerHTML}
      </body></html>
    `);
    win.document.close();
    win.print();
  };

  return (
    <div className="space-y-5">
      <div className="bg-white border border-[#E5E1DB] rounded-2xl p-5 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-bold text-[#3A322C]">AI Complaint Pattern Analysis</p>
            <p className="text-sm text-[#8A7E73]">Detects schools with 3+ complaints or recurring issues in last 30 days.</p>
          </div>
          {result && (
            <button onClick={handlePrint}
              className="flex items-center gap-2 border border-[#E5E1DB] rounded-xl px-4 py-2 text-sm font-bold text-[#5C5147] hover:bg-[#F4F3F0]"
              data-testid="export-pdf-btn">
              <Printer size={15}/> Export PDF
            </button>
          )}
        </div>
        <button onClick={run} disabled={loading}
          className="w-full bg-[#3A322C] text-white rounded-xl py-3 font-bold flex items-center justify-center gap-2 disabled:opacity-60"
          data-testid="run-analysis-btn">
          {loading?<Loader size={16} className="animate-spin"/>:<Sparkles size={16}/>}
          {loading?"Analysing…":"Run Analysis"}
        </button>
      </div>

      {error && <div className="bg-[#FBEAE7] text-[#C2624E] rounded-2xl px-4 py-3 text-sm">{error}</div>}

      {result && (
        <div className="space-y-4" ref={printRef}>
          {result.ai_summary && (
            <div className="bg-[#F4F3F0] rounded-2xl px-5 py-4">
              <p className="text-xs font-bold uppercase text-[#8A7E73] mb-2">AI Summary</p>
              <p className="text-sm text-[#3A322C] leading-relaxed">{result.ai_summary}</p>
            </div>
          )}
          <p className="text-sm text-[#8A7E73]">{result.total_complaints} complaints analysed · {result.flagged_schools?.length||0} schools flagged</p>

          {result.flagged_schools?.length === 0 ? (
            <div className="bg-green-50 border border-green-200 rounded-2xl p-5 text-center text-green-700 font-bold" data-testid="no-flagged">
              No schools flagged in last 30 days.
            </div>
          ) : (result.flagged_schools||[]).map((s,i) => {
            const isRecurring = s.flags?.some(f=>f.includes("Recurring"));
            return (
              <div key={i}
                className={`rounded-2xl overflow-hidden border-l-4 ${isRecurring ? "border-red-500 bg-red-50/30" : "border-amber-400 bg-amber-50/30"} border border-[#E5E1DB]`}
                data-testid={`flagged-card-${i}`}>
                <div className="px-5 py-4">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <div className="flex items-center gap-2">
                        {isRecurring ? <Flag size={16} className="text-red-500"/> : <AlertTriangle size={16} className="text-amber-500"/>}
                        <p className="font-bold text-[#3A322C] text-base">{s.school_name}</p>
                      </div>
                      <p className="text-xs text-[#8A7E73] mt-0.5">Route {s.route_number} · {s.total_complaints} complaints · Top: {s.top_category}</p>
                    </div>
                    <div className="flex flex-col gap-1">
                      {(s.flags||[]).map((f,j) => (
                        <span key={j} className={`text-xs font-bold px-2 py-0.5 rounded-full ${f.includes("Recurring")?"bg-red-100 text-red-700":"bg-amber-100 text-amber-700"}`}>{f}</span>
                      ))}
                    </div>
                  </div>
                  {/* 30-day trend sparkline */}
                  {s.trend && (
                    <div className="mt-4">
                      <p className="text-xs text-[#8A7E73] font-bold mb-2">30-day complaint trend</p>
                      <ResponsiveContainer width="100%" height={80}>
                        <BarChart data={s.trend} margin={{top:0,right:0,bottom:0,left:0}}>
                          <Bar dataKey="count" fill={isRecurring?"#ef4444":"#f59e0b"} radius={[2,2,0,0]}/>
                          <XAxis dataKey="date" hide/>
                          <YAxis hide/>
                          <Tooltip
                            formatter={(v) => [v, "Complaints"]}
                            labelFormatter={(l) => l}
                            contentStyle={{fontSize:"11px",padding:"4px 8px"}}/>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── MAIN CALL CENTRE PAGE ────────────────────────────────────────────────────
export default function CallCentre() {
  const [tab, setTab] = useState("log");
  const [refresh, setRefresh] = useState(0);

  const TABS = [
    { id:"log",       icon:Phone,    label:"Log Call"  },
    { id:"dashboard", icon:BarChart3, label:"Dashboard" },
    { id:"analysis",  icon:Sparkles,  label:"Analysis"  },
  ];

  return (
    <div className="space-y-6 max-w-4xl" data-testid="call-centre">
      <div>
        <h1 className="text-3xl font-extrabold font-[Manrope] text-[#3A322C]">Call Centre</h1>
        <p className="text-sm text-[#8A7E73] mt-1">Daily school call log and complaint tracking</p>
      </div>

      <div className="flex border-b border-[#E5E1DB]">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-5 py-3 text-sm font-semibold flex items-center gap-2 whitespace-nowrap transition-colors -mb-px ${tab===t.id?"border-b-2 border-[#E8956D] text-[#E8956D]":"text-[#8A7E73] hover:text-[#5C5147]"}`}>
            <t.icon size={15}/>{t.label}
          </button>
        ))}
      </div>

      {tab==="log" && <LogCallTab onLogged={() => setRefresh(r=>r+1)}/>}
      {tab==="dashboard" && <DashboardTab refresh={refresh}/>}
      {tab==="analysis" && <AnalysisTab/>}
    </div>
  );
}
